import * as React from 'react';
import { useSearchParams } from 'react-router';
import { useAuth } from '../contexts/AuthContext';
import useNotifications from '../hooks/useNotifications';
import NotificationRow from '../components/notifications/NotificationRow';
import { Ic } from '../components/shell/Icons';
import {
  relativeTime, severityOf, STATE_PILL, TYPE_LABEL,
} from '../components/notifications/notifUtils';

const TYPE_OPTIONS = ['server', 'org', 'project', 'user', 'billing'];
const SEV_OPTIONS = ['critical', 'warning', 'info'];
const STATE_OPTIONS = ['all', 'active', 'unseen', 'seen', 'viewed', 'addressed', 'dismissed'];

// Render the `detail` payload (parsed JSON: log snippets + links + fields).
function DetailBlock({ detail }) {
  if (!detail) return null;
  if (typeof detail === 'string') {
    return <pre data-testid="notif-detail-raw">{detail}</pre>;
  }
  const { links, snippets, snippet, ...rest } = detail;
  const linkList = Array.isArray(links) ? links : [];
  const snippetList = Array.isArray(snippets) ? snippets : (snippet ? [snippet] : []);
  const hasRest = rest && Object.keys(rest).length > 0;
  return (
    <div style={{ marginTop: 10 }}>
      {snippetList.map((s) => {
        const text = typeof s === 'string' ? s : JSON.stringify(s, null, 2);
        return <pre key={text} data-testid="notif-detail-snippet">{text}</pre>;
      })}
      {hasRest && <pre data-testid="notif-detail-fields">{JSON.stringify(rest, null, 2)}</pre>}
      {linkList.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
          {linkList.map((l) => {
            const href = typeof l === 'string' ? l : (l.url || l.href);
            const label = typeof l === 'string' ? l : (l.label || l.title || href);
            return (
              <a key={href} href={href} target="_blank" rel="noreferrer"
                 className="tip-link" data-testid="notif-detail-link"
                 style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Ic name="link" sm /> {label}
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Settings → Notifications (#259). Filterable list with the state pill as the
 * primary column and the same scroll-into-view → `seen` behaviour as the
 * popover; selecting a row opens a detail view (marks it `viewed`) with body,
 * detail (log snippets / links) and recommendation, and footer actions
 * Mark addressed / Dismiss / Close.
 *
 * Only super-admins ever see `server` (platform-health) items — the backend
 * already withholds their receipts, and this filters them client-side too
 * (defence in depth) and drops `server` from the type filter for everyone else.
 */
export default function NotificationsSettings() {
  const { isSuperAdmin } = useAuth();
  const { items, loading, load, queueSeen, markViewed, act } = useNotifications({ poll: false });
  const [params] = useSearchParams();
  const focusId = params.get('focus');

  const [stateFilter, setStateFilter] = React.useState('all');
  const [typeFilter, setTypeFilter] = React.useState('all');
  const [sevFilter, setSevFilter] = React.useState('all');
  const [selected, setSelected] = React.useState(null);
  const [copied, setCopied] = React.useState(false);

  // #407: on mobile the detail panel stacks BELOW the list (ds.css column layout),
  // so tapping a row gave no feedback — scroll it into view when it opens on a
  // narrow viewport (same 820px breakpoint as the CSS switch).
  const detailRef = React.useRef(null);
  React.useEffect(() => {
    setCopied(false);
    const mq = typeof window !== 'undefined' && window.matchMedia
      && window.matchMedia('(max-width: 820px)');
    if (selected && detailRef.current && mq && mq.matches) {
      detailRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selected]);

  // #407: copy the full, actionable notification (title + body + detail + recommendation)
  // so an operator can paste it straight into an issue. Falls back gracefully where the
  // async clipboard API is unavailable (older / insecure contexts).
  const copyDetails = React.useCallback(async () => {
    if (!selected) return;
    const detailText = selected.detail == null ? ''
      : (typeof selected.detail === 'string' ? selected.detail : JSON.stringify(selected.detail, null, 2));
    const text = [
      selected.title, selected.body || '',
      detailText && `\nDetails:\n${detailText}`,
      selected.recommendation && `\nRecommendation: ${selected.recommendation}`,
    ].filter(Boolean).join('\n').trim();
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text; document.body.appendChild(ta); ta.select();
        document.execCommand('copy'); document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked — leave the button label unchanged */ }
  }, [selected]);

  const reload = React.useCallback(() => {
    load({ state: stateFilter, limit: 100 }).catch(() => { /* shown as empty */ });
  }, [load, stateFilter]);

  React.useEffect(() => { reload(); }, [reload]);

  const openDetail = React.useCallback(async (n) => {
    try {
      const full = await markViewed(n.id);
      setSelected(full || n);
    } catch {
      setSelected(n);
    }
  }, [markViewed]);

  // Deep-link from the bell popover ("View all"/row → ?focus=<id>).
  const focusedRef = React.useRef(null);
  React.useEffect(() => {
    if (focusId && focusedRef.current !== focusId && items.length) {
      const hit = items.find((n) => n.id === focusId);
      if (hit) { focusedRef.current = focusId; openDetail(hit); }
    }
  }, [focusId, items, openDetail]);

  const doAct = async (action) => {
    if (!selected) return;
    try { await act(selected.id, action); } catch { /* surfaced via reload */ }
    setSelected(null);
    reload();
  };

  const visible = items.filter((n) => {
    if (!isSuperAdmin && n.type === 'server') return false;
    if (typeFilter !== 'all' && n.type !== typeFilter) return false;
    if (sevFilter !== 'all' && n.severity !== sevFilter) return false;
    return true;
  });

  const typeChoices = TYPE_OPTIONS.filter((t) => isSuperAdmin || t !== 'server');

  return (
    <div>
      <h2 style={{ marginBottom: 4 }}>Notifications</h2>
      <p className="muted" style={{ marginTop: 0, marginBottom: 16 }}>
        Alerts and updates for you, your organisation and your projects.
      </p>

      <div className="notif-page">
        <div className="notif-list-col">
          <div className="notif-filters">
            <Ic name="filter" sm />
            <select className="input" data-testid="notif-filter-state"
                    value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
              {STATE_OPTIONS.map((s) => (
                <option key={s} value={s}>{s === 'all' ? 'All states' : (STATE_PILL[s]?.label || s)}</option>
              ))}
            </select>
            <select className="input" data-testid="notif-filter-type"
                    value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="all">All types</option>
              {typeChoices.map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
            </select>
            <select className="input" data-testid="notif-filter-severity"
                    value={sevFilter} onChange={(e) => setSevFilter(e.target.value)}>
              <option value="all">All severities</option>
              {SEV_OPTIONS.map((s) => <option key={s} value={s}>{severityOf(s).label}</option>)}
            </select>
          </div>

          <div className="cardp" style={{ padding: 0, overflow: 'hidden' }}>
            {loading && visible.length === 0 ? (
              <div className="notif-empty">Loading…</div>
            ) : visible.length === 0 ? (
              <div className="notif-empty" data-testid="notif-list-empty">No notifications.</div>
            ) : (
              visible.map((n) => (
                <NotificationRow key={n.id} n={n} showState onVisible={queueSeen}
                                 onClick={openDetail} active={selected?.id === n.id} />
              ))
            )}
          </div>
        </div>

        {selected && (
          <div className="notif-detail" ref={detailRef} style={{ flex: 1, minWidth: 0, maxWidth: 480 }}
               data-testid="notif-detail">
            <div className="notif-row-top" style={{ marginBottom: 8 }}>
              <span className={`dot ${severityOf(selected.severity).dot}`} aria-label={severityOf(selected.severity).label} />
              <span className="badge neutral notif-type">{TYPE_LABEL[selected.type] || selected.type}</span>
              <span className={`badge ${(STATE_PILL[selected.state] || STATE_PILL.viewed).badge}`}
                    data-testid="notif-detail-state">
                {(STATE_PILL[selected.state] || STATE_PILL.viewed).label}
              </span>
              <span className="grow" />
              <button type="button" className="btn ghost sm" data-testid="notif-detail-copy"
                      onClick={copyDetails} title="Copy notification details"
                      style={{ marginRight: 6 }}>
                <Ic name="copy" sm /> {copied ? 'Copied' : 'Copy'}
              </button>
              <span className="notif-time"><Ic name="clock" sm /> {relativeTime(selected.created_at)}</span>
            </div>
            <h3 style={{ margin: '4px 0 8px' }}>{selected.title}</h3>
            {selected.body && <div style={{ fontSize: 13, color: 'var(--text)' }}>{selected.body}</div>}
            <DetailBlock detail={selected.detail} />
            {selected.recommendation && (
              <div data-testid="notif-recommendation"
                   style={{
                     marginTop: 12, padding: '10px 12px', borderRadius: 'var(--r-sm)',
                     background: 'var(--accent-subtle)', border: '1px solid var(--border)',
                   }}>
                <div className="t-over" style={{ marginBottom: 4 }}>Recommendation</div>
                <div style={{ fontSize: 12.5, color: 'var(--text-2)' }}>{selected.recommendation}</div>
              </div>
            )}
            <div className="notif-detail-foot">
              <button type="button" className="btn primary" data-testid="notif-action-addressed"
                      onClick={() => doAct('addressed')}>
                <Ic name="check" sm /> Mark addressed
              </button>
              <button type="button" className="btn secondary" data-testid="notif-action-dismissed"
                      onClick={() => doAct('dismissed')}>
                Dismiss
              </button>
              <button type="button" className="btn ghost" data-testid="notif-action-close"
                      onClick={() => doAct('close')}>
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
